import { useEffect, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { makeAuthenticatedApiCall } from '../../../apiHelper';
import { collectDealProcurementPieces, removeUrlFromPieceGroups } from '../../../utils/cfnPieces';
import { pieceDetachmentEndpoints, requestPieceDetachment } from '../../../utils/pieceDetachment';
import usePreDialogValidation from '../../../hooks/usePreDialogValidation';
import {
  useDealMisivA,
  useDealMisivB,
  useDealMisivE,
  useDepositSaleCalculator,
  useMandateCalculator,
} from '../../../hooks/useApiQueries';
import theme from '../../../theme';
import { DBDeal } from '../../../types/deal';
import {
  documentTypes,
  documentTypesDb,
  formatDateToDDMMYYYY,
  getAdresseTypeVoie,
  getCleanPrice,
  getPipedriveDealData,
  getPipedriveOrgData,
  getPipedrivePersonData,
  removeSpaces,
  resolveVoieCode,
} from '../../../utils';
import { getCommonValidationRules } from '../../../utils/formValidationRules';
import FirebaseUploadComponent from '../../CreateCotation/FirebaseUploadComponent';
import { ErrorMessageSnackbar } from '../../ErrorMessageSnackbar';
import CfnPiecesWarning from './CfnPiecesWarning';
import ManualLPN from './ManualLPN';

interface SubmitPurchaseCertificate {
  deal: DBDeal;
  isMandate: boolean;
  isDepositSale?: boolean;
  onRefresh?: () => void;
  /** `car_deals.photos` : les pièces de démarche du deal, listées pour retrait avant soumission. */
  photos?: Record<string, string[]> | null;
}

const SubmitPurchaseCertificate: React.FC<SubmitPurchaseCertificate> = ({
  deal,
  isMandate,
  isDepositSale,
  onRefresh,
  photos,
}) => {
  // Pièces de démarche du deal (6 catégories du périmètre CFN, jamais les
  // photos du véhicule). Elles ont été déposées en amont (UploadModal /
  // UploadModalMandate) ; on les affiche ici pour que le vérificateur retire
  // celles qui n'appartiennent pas au véhicule avant d'envoyer au SIV.
  const [pieceGroups, setPieceGroups] = useState(() => collectDealProcurementPieces(photos));
  useEffect(() => {
    setPieceGroups(collectDealProcurementPieces(photos));
  }, [photos]);

  const handleBeforePieceDelete = (url: string) => requestPieceDetachment(pieceDetachmentEndpoints.deal(deal.id), url);

  const handlePieceDeleteComplete = (url: string) => {
    setPieceGroups((groups) => removeUrlFromPieceGroups(groups, url));
    onRefresh?.();
  };

  const [isLoading, setIsLoading] = useState(false);
  const [isLogbookSending, setIsLogbookSending] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [purchaseDateTime, setPurchaseDateTime] = useState('');
  const [responseErrorCode, setResponseErrorCode] = useState(null);
  const [buttonText, setButtonText] = useState<string>('');
  const [buttonStep, setButtonStep] = useState<string>('initial');
  const [responseNomFic, setResponseNomFic] = useState<string>('');
  const [responseId, setResponseId] = useState<number>();
  const [errorText, setErrorText] = useState<string>('');
  const [isTimeoutError, setIsTimeoutError] = useState(false);

  const validation = usePreDialogValidation();

  const [showLogbookButton, setShowLogbookButton] = useState(false);
  const [logbookConfirmation, setLogbookConfirmation] = useState<string>('');

  const [clientType, setClientType] = useState<string>('');
  const [niceAddress, setNiceAddress] = useState<Record<string, string | null>>({});
  const [ownerData, setOwnerData] = useState<Record<string, string | null>>({});
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [orgName, setOrgName] = useState<string>('');
  const [latestSellingPrice, setLatestSellingPrice] = useState<string | null>(null);
  const [vin, setVin] = useState<string>(deal.vin || '');
  const [numFormule, setNumFormule] = useState<string>(deal.num_formule || '');
  const [isSavingVehicleInfo, setIsSavingVehicleInfo] = useState(false);
  const [incompleteFieldsWarning, setIncompleteFieldsWarning] = useState<string[] | null>(null);

  // `deal` est un prop figé au chargement de la liste (rows construites depuis Pipedrive dans
  // DealsList.fetchData) : il ne reflète PAS les corrections faites dans cette modale, même après
  // « Enregistrer ». Les states `vin` / `numFormule` sont la seule source à jour — sans eux, la DA
  // repart au SIV avec l'ancienne valeur (incident AG-415-SZ du 22/08/2026 : 6 soumissions
  // rejetées avec le n° de formule erroné alors que la correction était bien en base).
  const updatedDeal = {
    ...deal,
    vin: vin || null,
    num_formule: numFormule || null,
    purchaseDateTime,
    isMandate,
    isDepositSale,
    ...(latestSellingPrice && { sellingPrice: latestSellingPrice }),
  };
  const { data: misivDealData, refetch: refetchMisivDealData } = isDepositSale
    ? useDealMisivE(deal.id)
    : isMandate
      ? useDealMisivB(deal.id)
      : useDealMisivA(deal.id);

  // Hooks pour récupérer le prix de vente depuis les calculateurs
  const { data: mandateCalculatorData, refetch: refetchMandateCalculator } = useMandateCalculator(deal.id);
  const { data: depositSaleCalculatorData, refetch: refetchDepositSaleCalculator } = useDepositSaleCalculator(deal.id);

  const pipelineLetter = isDepositSale ? 'e' : isMandate ? 'b' : 'a';

  const queryClient = useQueryClient();

  const formMethods = useForm({
    mode: 'all',
  });
  const { handleSubmit, control, formState, trigger, setValue } = formMethods;
  const { errors, isValid } = formState;

  const validationRules = getCommonValidationRules(formMethods);

  useEffect(() => {
    const determineButtonState = () => {
      const data = misivDealData?.data && misivDealData.data.getDealMisivFromDB[0];
      if (!data) return 'initial';

      const {
        // receipt_file_firebase_url,
        receipt_file_name,
        response_id,
        has_timeout,
        purchase_date_time,
        lpn_id,
        step,
        last_update,
      } = data;

      if (receipt_file_name && response_id && has_timeout === 0) {
        setResponseNomFic(receipt_file_name);
        setButtonText(`✅ DA n°${response_id}`);
        setIsTimeoutError(false);
        if (!lpn_id) {
          setShowLogbookButton(true);
        } else if (lpn_id && step === 2) {
          setLogbookConfirmation(`✅ Inscrit au LPN le : ${formatDateToDDMMYYYY(last_update)}`);
          setShowLogbookButton(false);
        }
        return 'receipt';
      } else if (!receipt_file_name && has_timeout === 1) {
        setResponseId(response_id);
        setIsTimeoutError(true);
        setButtonText(`Renvoyer au ministère n°${response_id}`);
        return 'timeout-error';
      } else if (!receipt_file_name && has_timeout === 0) {
        setPurchaseDateTime(purchase_date_time);
        setIsTimeoutError(false);
        setButtonText(`⚠️ DA n°${response_id}`);
        return 'save';
      }

      return 'initial';
    };

    const newState = determineButtonState();
    setButtonStep(newState);
  }, [misivDealData]);

  const handleConfirmClick = () => {
    setIsConfirmModalOpen(true); // Ouvrir la modale de confirmation au lieu de soumettre directement
  };

  const handleFinalConfirm = async () => {
    const formData = formMethods.getValues(); // Récupérer les données du formulaire actuel

    setIsConfirmModalOpen(false); // Fermer la modale de confirmation
    setIsLoading(true);
    try {
      await handleSave(formData);
      await handleSavePurchaseCertificate(); // Appeler la fonction pour soumettre
      // Fermer le dialog principal seulement en cas de succès
      handleDialogClose();
    } catch (error: any) {
      openSnackbar(
        error.response?.data?.details ||
          error.response?.data?.message ||
          error.message ||
          'Erreur lors de la soumission',
        'error',
      );
      // En cas d'erreur, le dialog principal reste ouvert pour que l'utilisateur puisse corriger
    } finally {
      setIsLoading(false);
    }
  };

  const checkVehicleOwnerInformations = async () => {
    // Reset du bandeau d'avertissement à chaque appel — il sera ré-armé si des champs manquent.
    setIncompleteFieldsWarning(null);
    const missingFields: string[] = [];

    if (isMandate) {
      setClientType('pro'); // Car c'est l'agence qui achète, et l'agence est un pro t'as vu

      // CHANGÉ: Récupérer depuis la BDD locale au lieu de Pipedrive
      // Cela permet de voir les modifications faites via le bouton "Enregistrer"
      try {
        const response = await makeAuthenticatedApiCall('get', `/api/owners/${pipelineLetter}/${deal.id}`);

        if (!response.data) {
          // FALLBACK: Si pas de données en BDD, récupérer depuis Pipedrive (best-effort)
          const pipedriveDealData = await getPipedriveDealData(deal.id);
          setOrgName(pipedriveDealData?.org_name);
          const orgId = pipedriveDealData?.org_id?.value;
          const personId = pipedriveDealData?.person_id?.value;
          const _purchaserData = orgId ? await getPipedriveOrgData(orgId).catch(() => null) : null;
          const agencyPersonData = personId ? await getPipedrivePersonData(personId).catch(() => null) : null;

          const _niceAddress = getAdresseTypeVoie(_purchaserData?.['address_route'] ?? '');
          setNiceAddress(_niceAddress);

          const purchaserData = {
            clientType: 'pro',
            firstName: agencyPersonData?.first_name ?? '',
            lastName: agencyPersonData?.last_name ?? '',
            dateOfBirth: null,
            placeOfBirth: null,
            addressNumber: _purchaserData?.address_street_number ?? '',
            addressExtension: _purchaserData?.address_subpremise ?? null,
            addressType: _niceAddress.typeVoie,
            addressName: _niceAddress.nomVoie,
            city: _purchaserData?.address_locality ?? '',
            postalCode: _purchaserData?.address_postal_code ?? '',
            country: _purchaserData?.address_country ?? '',
            gender: null,
            documentId: null,
            documentType: null,
            documentIssueDate: null,
            companyName:
              _purchaserData?.['d7f00bd23217519c4d134d312d1206bc4a7021c6'] ?? pipedriveDealData?.org_name ?? '',
            siret: _purchaserData?.['cd6bc84ef4b1392fb08f9fdb9071cccb2c96ce1f'] ?? '',
          };
          setOwnerData(purchaserData);

          if (!_purchaserData?.address_postal_code) missingFields.push("Code postal de l'agence");
          if (!_purchaserData?.address_locality) missingFields.push("Ville de l'agence");
          if (!_purchaserData?.address_country) missingFields.push("Pays de l'agence");
          if (!_purchaserData?.address_route) missingFields.push("Rue de l'agence");
        } else {
          // DONNÉES DEPUIS LA BDD LOCALE (prioritaire)
          const _ownerData = response.data as any;
          setOwnerData(_ownerData);
          setOrgName(_ownerData?.company_name);
          setClientType(_ownerData?.client_type);
          setNiceAddress(_ownerData?.address_type);

          if (!_ownerData.postal_code) missingFields.push('Code postal du titulaire');
        }
      } catch (error: any) {
        console.error('❌ [FRONTEND checkVehicleOwnerInformations] Error:', error);
        missingFields.push('Données du titulaire/agence indisponibles — complétez à la main');
      }
    } else {
      try {
        const response = await makeAuthenticatedApiCall('get', `/api/owners/${pipelineLetter}/${deal.id}`);

        if (!response.data) {
          missingFields.push('Aucune donnée titulaire en base — saisie manuelle requise');
        } else {
          const _ownerData = response.data as any;
          setOwnerData(_ownerData);
          setOrgName(_ownerData?.company_name);
          setClientType(_ownerData?.client_type);
          setNiceAddress(_ownerData?.address_type);

          // Particulier => Francois Grolleau : ownerData['cdbbea0bbb21d6bb4fa5ed9ffa679c2a817d5839_postal_code']
          // Pro => Adena Piscines : ownerData['address_postal_code'];
          if (!_ownerData.postal_code) missingFields.push('Code postal du titulaire');
        }
      } catch (error: any) {
        console.error('❌ Erreur récupération titulaire:', error);
        missingFields.push('Données du vendeur indisponibles — complétez à la main');
      }
    }

    if (missingFields.length > 0) {
      console.warn('⚠️ Champs incomplets pour la DA — ouverture de la modale en saisie manuelle:', missingFields);
      setIncompleteFieldsWarning(missingFields);
    }

    // Toujours autoriser l'ouverture de la modale — l'opérateur complète à la main si nécessaire.
    return false;
  };

  const openSnackbar = validation.openSnackbar;

  const handleDialogClose = () => {
    setIsDialogOpen(false);
  };

  const handleSaveVehicleInfo = async () => {
    setIsSavingVehicleInfo(true);

    // Récupérer toutes les données du formulaire
    const formData = formMethods.getValues();

    try {
      // 1. Sauvegarder TOUTES les données du formulaire (propriétaire, etc.)
      await handleSave(formData);

      // 2. Mise à jour VIN et num_formule dans car_deals
      await makeAuthenticatedApiCall('patch', `/api/deals/${deal.id}/vehicle-info`, {
        vin: vin || null,
        num_formule: numFormule || null,
      });

      // 3. Mise à jour VIN et num_formule dans Pipedrive
      await makeAuthenticatedApiCall('patch', `/api/pipedrive/deals/${deal.id}/vehicle-info`, {
        vin: vin || null,
        num_formule: numFormule || null,
      });

      // Note: handleSave affiche déjà un snackbar de succès, pas besoin d'en afficher un autre

      openSnackbar('✅ Informations du véhicule et du propriétaire mises à jour avec succès', 'success');

      // Mettre à jour les valeurs locales de VIN et num_formule immédiatement
      setVin(vin || '');
      setNumFormule(numFormule || '');
      setValue('sellingPrice', formData.sellingPrice); // Aussi mettre à jour sellingPrice dans le formulaire

      // Invalider ET refetch les queries pour une actualisation complète.
      // ⚠ Les clés doivent être celles déclarées dans useApiQueries : ['deals'] / ['carDeals']
      // n'existent nulle part, ces appels étaient donc des no-op silencieux.
      queryClient.invalidateQueries({ queryKey: ['useArbitratorDeals'] });
      queryClient.invalidateQueries({ queryKey: ['useDatabaseDeals'] });

      await queryClient.refetchQueries({ queryKey: ['useArbitratorDeals'] });
      await queryClient.refetchQueries({ queryKey: ['useDatabaseDeals'] });

      // Rafraîchir les données si onRefresh est fourni
      if (onRefresh) {
        onRefresh();
      }
    } catch (error: any) {
      console.error('❌ [FRONTEND] Erreur lors de la sauvegarde:', error);
      openSnackbar(error.response?.data?.error || error.message || 'Erreur lors de la sauvegarde', 'error');
    } finally {
      setIsSavingVehicleInfo(false);
    }
  };

  const handleSave = async (formData: any /* FormDataPerson | FormDataPro */) => {
    // Utiliser le prix de vente du formulaire, ou latestSellingPrice en fallback
    const sellingPrice = formData.sellingPrice || latestSellingPrice;

    const submissionData = {
      ...formData,
      documentType: documentTypesDb[formData.documentType] || formData.documentType, // Fallback sur la valeur originale au cas où
      ...(sellingPrice && { sellingPrice }), // Inclure le prix de vente (du formulaire ou du calculateur)
    };

    try {
      const responseSave = await makeAuthenticatedApiCall(
        'post',
        `/api/owners/${pipelineLetter}/${deal.id}`,
        submissionData,
      );
      openSnackbar('Les modifications ont bien été prises en compte', 'success');
      return responseSave; // Retourner la réponse pour confirmation
    } catch (error: any) {
      console.error('❌ [FRONTEND handleSave] Error occurred:', error);
      openSnackbar(error.response?.data?.message || error.message || 'Erreur lors de la sauvegarde', 'error');
      validation.setDisabledButton(false); // Pour pouvoir re-tenter sans recharger
      throw error; // Re-throw pour que handleSaveVehicleInfo puisse gérer l'erreur
    } finally {
      setResponseErrorCode(null);
    }
  };

  const handleSavePurchaseCertificate = async () => {
    setIsLoading(true);
    validation.setDisabledButton(true);

    try {
      const responseSave = await makeAuthenticatedApiCall('post', `/api/misiv/save/purchase`, updatedDeal);
      const responseSubmitId = responseSave?.data.responseId;
      setButtonStep('save');
      setButtonText(`⚠️ DA n°${responseSubmitId}`);
      handleSubmitPurchase();
    } catch (error: any) {
      console.error('Erreur lors de la sauvegarde de la demande:', error);
      // Le backend renvoie { message: '...', details: '...' }
      const errorMessage =
        error.response?.data?.details ||
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Erreur lors de la sauvegarde de la demande';
      openSnackbar(errorMessage, 'error');
      setErrorText(errorMessage);
      validation.setDisabledButton(false);
      setButtonStep('initial');
      setIsLoading(false);
    } finally {
      // setIsLoading(false);
    }
  };

  const handleSubmitPurchase = async () => {
    setIsLoading(true);
    validation.setDisabledButton(true);

    try {
      const responseSubmit = await makeAuthenticatedApiCall('post', `/api/misiv/submit/purchase`, updatedDeal);

      const responseId = responseSubmit.data.responseId;
      const responseNomFic = responseSubmit.data.responseNomFic;
      setResponseNomFic(responseNomFic);

      // Gérer le cas où il n'y a pas d'erreur
      openSnackbar(`Votre demande a bien été envoyée : ${responseId}`, 'success');
      setButtonStep('receipt');
      setButtonText(`✅ DA n°${responseId}`);

      /*
      const base64String = responseSubmit.data.askPurchaseReceipt;
      const blob = base64ToBlob(base64String, 'application/pdf');
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl);
      */
      const firebaseUrl = responseSubmit.data.firebaseReceipt.firebaseReceiptUrl;
      if (firebaseUrl) {
        // Si nous recevons une URL Firebase, ouvrez-la directement
        window.open(firebaseUrl);
      }

      queryClient.invalidateQueries({ queryKey: ['useDealMisivA'] });
      queryClient.invalidateQueries({ queryKey: ['useDealMisivB'] });
      queryClient.invalidateQueries({ queryKey: ['useDealMisivE'] });
      await refetchMisivDealData();
      handleSendToLogbook();
    } catch (error: any) {
      if (error.response && error.response.status === 500) {
        // Cas où le serveur a renvoyé une erreur 500 avec des données JSON
        const responseId = error.response.data?.responseId;
        const responseErrorCode = error.response.data?.responseErrorCode;
        const responseErrorMessage = error.response.data?.responseErrorMessage;

        if (responseErrorCode === 3 || responseErrorCode === 100) {
          setResponseId(responseId);
          setIsTimeoutError(true);
          setButtonText(`Renvoyer au ministère n°${responseId}`);
          openSnackbar(`Timeout Ministère : Ré-essayer dans quelques instants`, 'error');
          return;
        } else {
          // Enregistrez le code d'erreur pour une utilisation ultérieure
          setResponseErrorCode(responseErrorCode);
        }

        if (responseErrorCode) {
          openSnackbar(`Erreur ${responseErrorCode} : ${responseErrorMessage}`, 'error');
          setErrorText(responseErrorMessage);
        } else {
          console.error("Erreur lors de l'envoi du certificat:", error);
          openSnackbar(error.response.data.error, 'error');
          setErrorText(error.response.data.error);
        }
      } else if (axios.isAxiosError(error)) {
        console.error('Erreur réseau ou serveur :', error.message);
        openSnackbar('Erreur réseau ou serveur : ' + error.message, 'error');
      } else {
        console.error("Erreur inattendue lors de l'envoi du certificat à TMS :", error);
        openSnackbar(`Erreur lors de l'envoi du certificat à TMS`, 'error');
      }
      validation.setDisabledButton(false);
      return;
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendToLogbook = async () => {
    setIsLogbookSending(true);
    setShowLogbookButton(true);

    try {
      await makeAuthenticatedApiCall('post', `/api/misiv/send-to-logbook`, {
        updatedDeal,
        pipelineLetter,
      });
      setLogbookConfirmation('✅ Inscrit au LPN');
      setShowLogbookButton(false);
    } catch (error: any) {
      console.error(error);
      openSnackbar(error.response.data, 'error');
      setErrorText(error.response.data);
    } finally {
      setIsLogbookSending(false);
    }
  };

  const handleResendToMinistry = async (responseSubmitId: number) => {
    setIsLoading(true);

    try {
      const responseOpen = await makeAuthenticatedApiCall('post', `/api/misiv/open/purchase`, {
        responseSubmitId,
      });
      handleGetReceipt(responseOpen.data.jsonObj);
      setButtonStep('receipt');
      setButtonText(`✅ DA n°${responseSubmitId}`);
      validation.setTooltipError('');
      setResponseNomFic(responseOpen.data.jsonObj);
    } catch (error: any) {
      console.error("Erreur lors de l'ouverture du certificat à TMS :", error);
      openSnackbar(error.response.data.error, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGetReceipt = async (responseNomFic: string) => {
    setIsLoading(true);

    try {
      const responseReceipt = await makeAuthenticatedApiCall('post', `/api/misiv/receipt/purchase`, {
        responseNomFic,
        pipelineLetter,
        dealId: deal.id,
        typeDemarche: 'DA',
      });
      /*
        const base64String = responseReceipt.data.askPurchaseReceipt;

      // Convertir le base64 en Blob
      const blob = base64ToBlob(base64String, 'application/pdf');

      // Créer un URL pour le Blob
      const blobUrl = URL.createObjectURL(blob);

      // Ouvrir le PDF dans une nouvelle fenêtre
      window.open(blobUrl);
      */
      const firebaseUrl = responseReceipt.data.firebaseUrl;
      if (firebaseUrl) {
        // Si nous recevons une URL Firebase, ouvrez-la directement
        window.open(firebaseUrl);
      }
      setButtonStep('receipt');
      setIsTimeoutError(false);
    } catch (error: any) {
      console.error('handleGetReceipt - Erreur lors de la génération du reçu :', error);
      openSnackbar(error.response.data.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleButtonClick = async () => {
    const hasError = await checkVehicleOwnerInformations();

    if (!hasError) {
      validation.resetValidation();
      if (buttonText.startsWith('⚠️ DA n°') && responseErrorCode === 3 && responseErrorCode === 100) {
        // Comportement par défaut
        handleSubmitPurchase();
      } else {
        setIsDialogOpen(true);
      }
    }
  };

  // Initialiser VIN et num_formule UNE SEULE FOIS au montage du composant
  useEffect(() => {
    setVin(deal.vin || '');
    setNumFormule(deal.num_formule || '');
  }, []); // Empty dependency array = run only once on mount

  useEffect(() => {
    if (isDialogOpen) {
      trigger(); // Déclencher la validation de tous les champs

      // Refetch des données du calculateur pour avoir le prix de vente le plus récent
      const refetchCalculatorData = async () => {
        try {
          let latestSellingPrice = null;

          if (isMandate) {
            // Pipeline B : refetch mandate calculator
            const { data: updatedMandateData } = await refetchMandateCalculator();
            latestSellingPrice = updatedMandateData?.selling_final_price;
          } else if (isDepositSale) {
            // Pipeline E : refetch deposit sale calculator
            const { data: updatedDepositSaleData } = await refetchDepositSaleCalculator();
            latestSellingPrice = updatedDepositSaleData?.sale_price;
          }

          // Mettre à jour le deal avec le prix de vente le plus récent
          if (latestSellingPrice) {
            setLatestSellingPrice(latestSellingPrice);
            // Mettre à jour le champ du formulaire avec le nouveau prix
            setValue('sellingPrice', latestSellingPrice);
          }
        } catch (error) {
          console.error('SubmitPurchaseCertificate - Error refetching calculator data:', error);
        }
      };

      refetchCalculatorData();
    }
  }, [
    isDialogOpen,
    trigger,
    isMandate,
    isDepositSale,
    refetchMandateCalculator,
    refetchDepositSaleCalculator,
    setValue,
  ]);

  useEffect(() => {
    if (ownerData) {
      formMethods.reset({
        clientType: ownerData.client_type,
        firstName: ownerData.first_name,
        lastName: ownerData.last_name,
        dateOfBirth: ownerData.date_of_birth,
        addressNumber: ownerData.address_number,
        addressExtension: ownerData.address_extension,
        // Normalise le code stocké (legacy che/chm, MAJUSCULE, libellé) vers le
        // code du dropdown pour l'afficher au lieu de « Inconnu ».
        addressType: ownerData.address_type ? resolveVoieCode(ownerData.address_type) : '',
        addressName: ownerData.address_name,
        city: ownerData.city,
        postalCode: ownerData.postal_code,
        country: ownerData.country,
        gender: ownerData.gender,
        documentType: ownerData.document_type,
        documentIssueDate: ownerData.document_issue_date,
        documentId: ownerData.document_id,
        siret: ownerData.siret,
        companyName: ownerData.company_name,
        placeOfBirth: ownerData.place_of_birth,
        // Assurez-vous d'ajouter ou de retirer des champs en fonction de ce qui est pertinent pour votre formulaire.
      });
    }
  }, [ownerData, formMethods]);

  return (
    <>
      {buttonStep === 'initial' && !isTimeoutError && (
        <div style={{ position: 'relative' }}>
          <Tooltip title={validation.tooltipError ? validation.tooltipError : ''} placement="top">
            <div>
              <Button
                sx={{
                  fontSize: 14,
                  margin: '8px auto 0',
                  maxWidth: 180,
                  color: 'white',
                  backgroundColor: validation.tooltipError ? 'red' : '',
                }}
                variant="contained"
                onClick={() => handleButtonClick()}
                disabled={validation.disabledButton || !!validation.tooltipError}
                fullWidth
              >
                {isLoading && <CircularProgress size={24} style={{ color: 'white', marginRight: 1 }} />}
                {validation.disabledButton ? buttonText : '📩 Soumettre la DA'}
              </Button>
            </div>
          </Tooltip>
          {validation.tooltipError && (
            <Typography
              color="error"
              sx={{
                fontSize: 12,
                marginTop: 1,
                textAlign: 'center',
              }}
            >
              {validation.tooltipError}
            </Typography>
          )}
        </div>
      )}

      {buttonStep === 'save' && !isTimeoutError && (
        <Tooltip
          title={
            isLoading
              ? "Demande sauvegardée, maintenant en cours d'envoi"
              : "La demande n'a pas abouti. Cliquez pour réessayer."
          }
          placement="top"
        >
          <div>
            <Button
              sx={{
                fontSize: 14,
                margin: '8px auto 0',
                width: 180,
                backgroundColor: 'orange !important',
              }}
              variant="contained"
              onClick={
                responseErrorCode !== 3 && responseErrorCode !== 100
                  ? () => handleButtonClick()
                  : () => handleSubmitPurchase()
              }
              fullWidth
            >
              {isLoading && <CircularProgress size={12} style={{ color: 'white' }} />}
              {buttonText}
            </Button>
            {errorText && (
              <Typography
                sx={{ fontSize: '10px', lineHeight: '1.25', marginTop: 1, fontWeight: 'bold', color: 'red' }}
                color="red"
              >
                {errorText}
              </Typography>
            )}
          </div>
        </Tooltip>
      )}

      {buttonStep === 'receipt' && !isTimeoutError && (
        <Button
          sx={{
            fontSize: 14,
            margin: '8px auto 0',
            maxWidth: 160,
            backgroundColor: 'green',
            color: 'white',
          }}
          variant="contained"
          onClick={() => handleGetReceipt(responseNomFic)}
          fullWidth
        >
          {isLoading && <CircularProgress size={12} style={{ color: 'white' }} />}
          {buttonText}
        </Button>
      )}
      {isTimeoutError && responseId !== undefined && (
        <Tooltip title="Timeout Ministère : Ré-essayez dans quelques instants" placement="top">
          <div>
            <Button
              sx={{
                fontSize: 14,
                margin: '8px auto 0',
                width: 180,
                backgroundColor: 'red !important',
              }}
              variant="contained"
              onClick={() => handleResendToMinistry(responseId)}
              fullWidth
            >
              {isLoading && <CircularProgress size={12} style={{ color: 'white' }} />}
              {buttonText}
            </Button>
            {errorText && (
              <Typography
                sx={{ fontSize: '10px', lineHeight: '1.25', marginTop: 1, fontWeight: 'bold', color: 'red' }}
                color="red"
              >
                {errorText}
              </Typography>
            )}
          </div>
        </Tooltip>
      )}
      {showLogbookButton && (
        <>
          <Button
            onClick={handleSendToLogbook}
            sx={{
              margin: '8px auto 0',
              width: 180,
              backgroundColor: 'orange !important',
            }}
            variant="contained"
          >
            {isLogbookSending ? (
              <>
                <CircularProgress size={24} style={{ color: 'white', marginRight: 1 }} /> Envoi au LPN en cours
              </>
            ) : (
              '⚠️ Renvoyer au LPN'
            )}
          </Button>
        </>
      )}
      {!logbookConfirmation?.startsWith('✅ Inscrit au LPN le : ') && (
        <ManualLPN
          id={misivDealData?.data?.getDealMisivFromDB[0]?.id}
          pipelineLetter={pipelineLetter}
          onSuccess={() => {
            setLogbookConfirmation(`✅ Inscrit au LPN manuellement`);
            setShowLogbookButton(false);
            queryClient.invalidateQueries({ queryKey: [`useDealMisiv${pipelineLetter.toUpperCase()}`] });
          }}
        />
      )}
      {logbookConfirmation && (
        <Typography variant="body2" align="center" sx={{ marginTop: 1, fontSize: 16 }}>
          {logbookConfirmation}
        </Typography>
      )}
      <Dialog open={isDialogOpen} onClose={handleDialogClose} fullWidth maxWidth="lg">
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(handleSave)}>
            <DialogContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={12} sx={{ textAlign: 'center' }}>
                  <TextField
                    autoFocus
                    margin="dense"
                    id="purchaseDateTime"
                    label="Date et heure de la vente"
                    type="datetime-local"
                    fullWidth
                    value={purchaseDateTime || ''}
                    onChange={(e) => setPurchaseDateTime(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    required
                  />
                  <Typography variant="body1" textAlign="center">
                    Veuillez vérifier les informations provenants de Skipcar.
                  </Typography>
                  <Typography variant="body2" textAlign="center">
                    Nous les utilisons pour faire l'envoi au SIV.
                  </Typography>
                </Grid>
                {incompleteFieldsWarning && incompleteFieldsWarning.length > 0 && (
                  <Grid item xs={12}>
                    <Alert severity="warning" sx={{ whiteSpace: 'pre-line' }}>
                      <strong>Données incomplètes :</strong>
                      {'\n'}
                      {incompleteFieldsWarning.map((f) => `• ${f}`).join('\n')}
                      {'\n\n'}
                      Complétez les champs manquants ci-dessous — vos saisies seront enregistrées et primeront sur
                      Pipedrive pour les soumissions futures.
                    </Alert>
                  </Grid>
                )}

                {/* Section Informations du véhicule */}
                <Grid item xs={12} md={6}>
                  <TextField
                    className="inputElement"
                    margin="dense"
                    id="vin"
                    label="VIN (Numéro de série)"
                    type="text"
                    fullWidth
                    value={vin}
                    onChange={(e) => setVin(e.target.value.toUpperCase())}
                    inputProps={{ maxLength: 17 }}
                    helperText="17 caractères alphanumériques"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    className="inputElement"
                    margin="dense"
                    id="numFormule"
                    label="Numéro de formule"
                    type="text"
                    fullWidth
                    value={numFormule}
                    onChange={(e) => setNumFormule(e.target.value)}
                    inputProps={{ maxLength: 50 }}
                    helperText="Numéro de formule de la carte grise"
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <Controller
                    name="sellingPrice"
                    control={control}
                    defaultValue={getCleanPrice(deal)}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        error={!!errors.sellingPrice}
                        className="inputElement"
                        margin="dense"
                        id="sellingPrice"
                        label="Prix de vente"
                        type="text"
                        fullWidth
                        InputProps={{
                          endAdornment: (
                            <Typography variant="body2" style={{ fontWeight: 'normal' }}>
                              €
                            </Typography>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Controller
                    name="clientType"
                    control={control}
                    defaultValue={clientType ?? ''}
                    rules={{ required: 'Ce champ est requis' }}
                    render={({ field, fieldState: { error } }) => (
                      <FormControl component="fieldset" margin="dense" error={!!error} fullWidth>
                        <FormLabel component="legend" sx={{ fontSize: '0.75rem' }}>
                          Type de client
                        </FormLabel>
                        <RadioGroup
                          {...field}
                          row
                          id="clientType"
                          value={field.value || ''}
                          onChange={(e) => {
                            const newValue = e.target.value;
                            field.onChange(newValue);
                            setClientType(newValue);
                          }}
                        >
                          <FormControlLabel value="person" control={<Radio size="small" />} label="Particulier" />
                          <FormControlLabel value="pro" control={<Radio size="small" />} label="Professionnel" />
                        </RadioGroup>
                        {error && <FormHelperText>{error.message}</FormHelperText>}
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Controller
                    name="lastName"
                    control={control}
                    rules={validationRules.lastName}
                    defaultValue={clientType === 'pro' ? ownerData?.lastName : (ownerData?.last_name ?? '')}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        id="lastName"
                        error={!!errors.lastName}
                        className="inputElement"
                        margin="dense"
                        label="Nom"
                        type="text"
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Controller
                    name="firstName"
                    control={control}
                    rules={validationRules.firstName}
                    defaultValue={clientType === 'pro' ? ownerData?.firstName : (ownerData?.first_name ?? '')}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        error={!!errors.firstName}
                        className="inputElement"
                        margin="dense"
                        id="firstName"
                        label="Prénom"
                        type="text"
                        fullWidth
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={2}>
                  <Controller
                    name="addressNumber"
                    control={control}
                    rules={validationRules.addressNumber}
                    defaultValue={
                      clientType === 'pro'
                        ? isMandate
                          ? ownerData?.addressNumber
                          : ownerData['address_street_number']
                        : (ownerData['cdbbea0bbb21d6bb4fa5ed9ffa679c2a817d5839_street_number'] ?? '')
                    }
                    render={({ field }) => (
                      <TextField
                        {...field}
                        error={!!errors.addressNumber}
                        className="inputElement"
                        margin="dense"
                        id="addressNumber"
                        label="Numéro d'adresse"
                        type="text"
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Controller
                    name="addressExtension"
                    control={control}
                    defaultValue={
                      clientType === 'pro'
                        ? isMandate
                          ? ownerData?.addressExtension
                          : ownerData['address_street_subpremise']
                        : (ownerData['cdbbea0bbb21d6bb4fa5ed9ffa679c2a817d5839_subpremise'] ?? '')
                    }
                    render={({ field }) => (
                      <TextField
                        {...field}
                        className="inputElement"
                        margin="dense"
                        id="addressExtension"
                        label="Extension d'adresse"
                        type="text"
                        fullWidth
                        inputProps={{ maxLength: 10 }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Controller
                    name="addressType"
                    control={control}
                    rules={validationRules.addressType}
                    defaultValue={niceAddress?.typeVoie ?? ''}
                    render={({ field, fieldState: { error } }) => (
                      <FormControl fullWidth error={!!error}>
                        <InputLabel className="inputElement" id={`${field.name}-label`}>
                          Type d'adresse
                        </InputLabel>
                        <Select
                          sx={{ minHeight: 'inherit', height: '56px', marginTop: '8px' }}
                          className="inputElement"
                          {...field}
                          labelId={`${field.name}-label`}
                          id={field.name}
                          label="Type d'adresse"
                          value={field.value ? field.value.toLowerCase() : ''}
                        >
                          <MenuItem value="rue">Rue</MenuItem>
                          <MenuItem value="all">Allée</MenuItem>
                          <MenuItem value="avn">Avenue</MenuItem>
                          <MenuItem value="blvd">Boulevard</MenuItem>
                          <MenuItem value="chau">Chaussée</MenuItem>
                          <MenuItem value="che">Chemin</MenuItem>
                          <MenuItem value="cour">Cour</MenuItem>
                          <MenuItem value="fbg">Faubourg</MenuItem>
                          <MenuItem value="fort">Fort</MenuItem>
                          <MenuItem value="imp">Impasse</MenuItem>
                          <MenuItem value="ldit">Lieu-Dit</MenuItem>
                          <MenuItem value="parc">Parc</MenuItem>
                          <MenuItem value="pass">Passage</MenuItem>
                          <MenuItem value="plc">Place</MenuItem>
                          <MenuItem value="quai">Quai</MenuItem>
                          <MenuItem value="rte">Route</MenuItem>
                          <MenuItem value="sent">Sentier</MenuItem>
                          <MenuItem value="squa">Square</MenuItem>
                          <MenuItem value="sans">Inconnu</MenuItem>
                        </Select>
                        {error && <FormHelperText>{error.message}</FormHelperText>}
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="addressName"
                    control={control}
                    rules={validationRules.addressName}
                    defaultValue={niceAddress?.nomVoie ?? ''}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        className="inputElement"
                        error={!!errors.addressName}
                        margin="dense"
                        id="addressName"
                        label="Nom de la voie (Rue, Allée, ou Avenue, ne doivent pas apparaitre ici)"
                        type="text"
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name="city"
                    control={control}
                    rules={validationRules.city}
                    defaultValue={
                      clientType === 'pro'
                        ? isMandate
                          ? ownerData?.city
                          : ownerData['address_locality']
                        : (ownerData['cdbbea0bbb21d6bb4fa5ed9ffa679c2a817d5839_locality'] ?? '')
                    }
                    render={({ field }) => (
                      <TextField
                        {...field}
                        error={!!errors.city}
                        className="inputElement"
                        margin="dense"
                        id="city"
                        label="Ville"
                        type="text"
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Controller
                    name="postalCode"
                    control={control}
                    rules={validationRules.postalCode}
                    defaultValue={
                      clientType === 'pro'
                        ? isMandate
                          ? ownerData?.postalCode
                          : ownerData['address_postal_code']
                        : (ownerData['cdbbea0bbb21d6bb4fa5ed9ffa679c2a817d5839_postal_code'] ?? '')
                    }
                    render={({ field }) => (
                      <TextField
                        {...field}
                        className="inputElement"
                        error={!!errors.postalCode}
                        margin="dense"
                        id="postalCode"
                        label="Code postal"
                        type="text"
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Controller
                    name="country"
                    control={control}
                    rules={validationRules.country}
                    defaultValue={
                      clientType === 'pro'
                        ? isMandate
                          ? ownerData?.country
                          : ownerData['address_country']
                        : (ownerData['cdbbea0bbb21d6bb4fa5ed9ffa679c2a817d5839_country'] ?? '')
                    }
                    render={({ field }) => (
                      <TextField
                        {...field}
                        className="inputElement"
                        error={!!errors.country}
                        margin="dense"
                        id="country"
                        label="Pays"
                        type="text"
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                {clientType === 'person' && (
                  <>
                    <Grid item xs={12} md={3}>
                      <Controller
                        name="dateOfBirth"
                        rules={clientType === 'person' ? { required: 'Ce champ est requis' } : {}}
                        control={control}
                        defaultValue={ownerData?.date_of_birth ?? ''}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            id="dateOfBirth"
                            error={!!errors.dateOfBirth}
                            sx={{ marginTop: '8px' }}
                            className="inputElement"
                            label="Date de naissance"
                            type="date"
                            fullWidth
                            margin="normal"
                            InputLabelProps={{ shrink: true }}
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Controller
                        name="placeOfBirth"
                        control={control}
                        rules={clientType === 'person' ? { required: 'Ce champ est requis' } : {}}
                        defaultValue={ownerData?.place_of_birth ?? ''}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            error={!!errors.placeOfBirth}
                            className="inputElement"
                            margin="dense"
                            id="placeOfBirth"
                            label="Lieu de naissance"
                            type="text"
                            fullWidth
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Controller
                        name="gender"
                        control={control}
                        rules={clientType === 'person' ? { required: 'Ce champ est requis' } : {}}
                        defaultValue={ownerData?.gender}
                        render={({ field }) => (
                          <Select
                            {...field}
                            error={!!errors.gender}
                            sx={{ minHeight: 'inherit', height: '56px', marginTop: '8px' }}
                            margin="dense"
                            fullWidth
                            className="inputElement"
                          >
                            <MenuItem value="male">Masculin</MenuItem>
                            <MenuItem value="female">Féminin</MenuItem>
                          </Select>
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Controller
                        name="documentId"
                        rules={clientType === 'person' ? { required: 'Ce champ est requis' } : {}}
                        control={control}
                        defaultValue={ownerData?.document_id ?? ''}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            margin="dense"
                            error={!!errors.documentId}
                            className="inputElement"
                            id="documentId"
                            label="ID du document"
                            type="text"
                            fullWidth
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Controller
                        name="documentType"
                        rules={clientType === 'person' ? { required: 'Ce champ est requis' } : {}}
                        control={control}
                        defaultValue={
                          ownerData?.['34387af45e0ae48b44f61195ee2f755a00f2280b']
                            ? documentTypes[ownerData?.['34387af45e0ae48b44f61195ee2f755a00f2280b']]?.dbValue || '' // Utiliser dbValue comme valeur par défaut
                            : ''
                        }
                        render={({ field }) => (
                          <Select
                            {...field}
                            sx={{ minHeight: 'inherit', height: '56px', marginTop: '0' }}
                            error={!!errors.documentType}
                            fullWidth
                            margin="dense"
                            className="inputElement"
                          >
                            <MenuItem value="id_card">Carte d'identité</MenuItem>
                            <MenuItem value="passport">Passeport</MenuItem>
                            <MenuItem value="driving_licence">Permis de conduire</MenuItem>
                            <MenuItem value="residence_permit">Titre de séjour</MenuItem>
                          </Select>
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Controller
                        name="documentIssueDate"
                        rules={clientType === 'person' ? { required: 'Ce champ est requis' } : {}}
                        control={control}
                        defaultValue={ownerData?.document_issue_date ?? ''}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            sx={{ minHeight: 'inherit', height: '56px', marginTop: '0' }}
                            error={!!errors.documentIssueDate}
                            className="inputElement"
                            id="documentIssueDate"
                            label="Date de délivrance du document"
                            type="date"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                          />
                        )}
                      />
                    </Grid>
                  </>
                )}
                {clientType === 'pro' && (
                  <>
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="siret"
                        control={control}
                        rules={
                          clientType === 'pro'
                            ? {
                                required: 'Ce champ est requis',
                                minLength: {
                                  value: 14,
                                  message: 'Le SIRET doit contenir exactement 14 caractères',
                                },
                                maxLength: {
                                  value: 14,
                                  message: 'Le SIRET doit contenir exactement 14 caractères',
                                },
                              }
                            : {
                                minLength: {
                                  value: 14,
                                  message: 'Le SIRET doit contenir exactement 14 caractères',
                                },
                                maxLength: {
                                  value: 14,
                                  message: 'Le SIRET doit contenir exactement 14 caractères',
                                },
                              }
                        }
                        defaultValue={
                          isMandate ? ownerData?.siret : (ownerData['cd6bc84ef4b1392fb08f9fdb9071cccb2c96ce1f'] ?? '')
                        }
                        render={({ field, fieldState: { error } }) => (
                          <TextField
                            {...field}
                            className="inputElement"
                            error={!!error}
                            helperText={error ? error.message : ''}
                            margin="dense"
                            id="siret"
                            label="SIRET"
                            type="text"
                            fullWidth
                            inputProps={{ maxLength: 14 }} // Limite le nombre de caractères que l'utilisateur peut taper
                            onChange={(e) => field.onChange(removeSpaces(e.target.value))} // Enlève les espaces
                          />
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Controller
                        name="companyName"
                        control={control}
                        rules={clientType === 'pro' ? { required: 'Ce champ est requis' } : {}}
                        defaultValue={
                          isMandate
                            ? ownerData?.companyName
                            : (ownerData['d7f00bd23217519c4d134d312d1206bc4a7021c6'] ?? orgName)
                        }
                        render={({ field }) => (
                          <TextField
                            {...field}
                            margin="dense"
                            error={!!errors.companyName}
                            id="companyName"
                            className="inputElement"
                            label="Raison sociale"
                            type="text"
                            fullWidth
                          />
                        )}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <Typography variant="body1">Pièces de la démarche</Typography>
                  <CfnPiecesWarning />
                  {pieceGroups.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      Aucune pièce de démarche déposée sur ce dossier.
                    </Typography>
                  )}
                </Grid>
                {pieceGroups.map((group) => (
                  <Grid item xs={12} md={6} key={group.key}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {group.label}
                    </Typography>
                    <FirebaseUploadComponent
                      hideDropzone
                      onBeforeDelete={handleBeforePieceDelete}
                      onDeleteComplete={handlePieceDeleteComplete}
                      uploadedUrls={group.urls}
                    />
                  </Grid>
                ))}
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button className="secondaryDealActionButton" onClick={handleDialogClose}>
                Annuler
              </Button>
              <Button
                className="dealActionButton"
                variant="outlined"
                onClick={handleSaveVehicleInfo}
                disabled={isSavingVehicleInfo}
                sx={{
                  border: '2px solid #FF6060 !important',
                  color: 'black !important',
                  backgroundColor: 'white !important',
                  '&:hover': {
                    backgroundColor: '#fff5f5 !important',
                    border: '2px solid #FF6060 !important',
                  },
                }}
              >
                {isSavingVehicleInfo ? (
                  <>
                    <CircularProgress size={20} style={{ marginRight: 8 }} />
                    Enregistrement...
                  </>
                ) : (
                  '💾 Enregistrer'
                )}
              </Button>
              <Button
                className="dealActionButton"
                variant="contained"
                onClick={handleConfirmClick}
                disabled={!isValid || !purchaseDateTime || isLoading}
              >
                {isLoading ? (
                  <>
                    <CircularProgress size={20} style={{ marginRight: 8, color: 'white' }} />
                    Soumission...
                  </>
                ) : (
                  'Soumettre'
                )}
              </Button>
            </DialogActions>
          </form>
        </FormProvider>
      </Dialog>
      {/* Modale de confirmation */}
      <Dialog open={isConfirmModalOpen} onClose={() => setIsConfirmModalOpen(false)}>
        <DialogContent>
          <Typography>Êtes-vous sûr que toutes les informations sont correctes ?</Typography>
        </DialogContent>
        <DialogActions>
          <Button className="secondaryDealActionButton" onClick={() => setIsConfirmModalOpen(false)}>
            Annuler
          </Button>
          <Button className="dealActionButton" onClick={handleFinalConfirm}>
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>
      <ErrorMessageSnackbar
        open={validation.snackbarOpen}
        message={validation.snackbarMessage}
        handleClose={() => validation.setSnackbarOpen(false)}
        severity={validation.severity}
      />
    </>
  );
};
export default SubmitPurchaseCertificate;
